import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { usePWA } from '../hooks/usePWA';

const PWAStatus = () => {
  const { isOnline, isInstalled } = usePWA();
  const [cacheSize, setCacheSize] = useState(0);
  const [serviceWorkerStatus, setServiceWorkerStatus] = useState('checking');

  useEffect(() => {
    checkServiceWorkerStatus();
    calculateCacheSize();
  }, []);

  const checkServiceWorkerStatus = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          setServiceWorkerStatus('active');
        } else {
          setServiceWorkerStatus('inactive');
        }
      } catch (error) {
        setServiceWorkerStatus('error');
      }
    } else {
      setServiceWorkerStatus('unsupported');
    }
  };

  const calculateCacheSize = async () => {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        let totalSize = 0;
        
        for (const cacheName of cacheNames) {
          const cache = await caches.open(cacheName);
          const keys = await cache.keys();
          totalSize += keys.length;
        }
        
        setCacheSize(totalSize);
      } catch (error) {
        console.log('Error calculating cache size:', error);
      }
    }
  };

  const clearCache = async () => {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        setCacheSize(0);
        
        // Reload to refetch resources
        window.location.reload();
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
      <h3 className="text-white font-medium mb-4 flex items-center">
        <AlertCircle className="text-rose-400 mr-2" size={18} />
        App Status
      </h3>
      
      <div className="space-y-3">
        {/* Connection Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isOnline ? (
              <Wifi className="text-green-400" size={16} />
            ) : (
              <WifiOff className="text-amber-400" size={16} />
            )}
            <span className="text-gray-300 text-sm">Connection</span>
          </div>
          <span className={`text-xs px-2 py-1 rounded ${
            isOnline ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
          }`}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>

        {/* Installation Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              isInstalled ? 'bg-green-400' : 'bg-gray-400'
            }`}></div>
            <span className="text-gray-300 text-sm">App Installation</span>
          </div>
          <span className={`text-xs px-2 py-1 rounded ${
            isInstalled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
          }`}>
            {isInstalled ? 'Installed' : 'Browser'}
          </span>
        </div>

        {/* Service Worker Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              serviceWorkerStatus === 'active' ? 'bg-green-400' : 
              serviceWorkerStatus === 'inactive' ? 'bg-amber-400' : 'bg-red-400'
            }`}></div>
            <span className="text-gray-300 text-sm">Service Worker</span>
          </div>
          <span className={`text-xs px-2 py-1 rounded ${
            serviceWorkerStatus === 'active' ? 'bg-green-500/20 text-green-400' : 
            serviceWorkerStatus === 'inactive' ? 'bg-amber-500/20 text-amber-400' : 
            'bg-red-500/20 text-red-400'
          }`}>
            {serviceWorkerStatus}
          </span>
        </div>

        {/* Cache Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-blue-400"></div>
            <span className="text-gray-300 text-sm">Cached Files</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">
              {cacheSize} files
            </span>
            {cacheSize > 0 && (
              <button
                onClick={clearCache}
                className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
                title="Clear cache and reload"
              >
                <RefreshCw size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {!isOnline && (
        <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-amber-300 text-xs">
            You're offline, but the app is working from cached content. Some features may be limited.
          </p>
        </div>
      )}
    </div>
  );
};

export default PWAStatus;